import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { GuestDirectoryContent as GuestDirectoryContentType } from 'components/GuestDirectory/types';
import GuestDirectoryExternalLinkButton from 'components/GuestDirectory/ExternalLinkButton';
import {
  ContentRowWrapper,
  TextColumn,
  ContentCopy,
  ContentTitle,
  ContentText,
  ContentImage,
  ContentButtonContainer,
  ShowMoreButtonInline,
} from './styles';

interface Props {
  content: GuestDirectoryContentType;
  locale: string;
  rowColour?: string | null;
  alternateRowColour?: string | null;
  isEven?: boolean;
  linkButtonColour?: string | null;
  linkButtonBg?: string | null;
  linkButtonBorder?: string | null;
}

const MAX_CHARACTERS = 250;

const GuestDirectoryContentRow: React.FC<Props> = ({
  content,
  locale,
  rowColour,
  alternateRowColour,
  isEven,
  linkButtonColour,
  linkButtonBg,
  linkButtonBorder,
}) => {
  const contentText = content.translations?.[locale]?.content || content.content || '';
  const buttonText = content.translations?.[locale]?.button_text || content.button_text;
  const image = content.images?.[0];

  const imageAlignMode = content.image_align_mode || 'left';
  const imageOnRight = imageAlignMode === 'right';
  const imageCentered = imageAlignMode === 'center';
  const hasImage = !!image;

  const [isExpanded, setIsExpanded] = useState(false);
  const showShowMore = contentText.length > MAX_CHARACTERS;
  const displayedText = showShowMore && !isExpanded ? contentText.substring(0, MAX_CHARACTERS) : contentText;

  let imageClassName = 'align-left';
  if (imageCentered) {
    imageClassName = 'align-center';
  } else if (imageOnRight) {
    imageClassName = 'align-right';
  }

  const backgroundColor = isEven ? alternateRowColour || rowColour || undefined : rowColour || undefined;

  return (
    <ContentRowWrapper backgroundColor={backgroundColor}>
      <TextColumn>
        <ContentCopy>
          {hasImage && (
            <ContentImage src={image.path} alt={image.alt_text || ''} className={imageClassName} />
          )}
          <ContentTitle>{content.name}</ContentTitle>
          {contentText && (
            <ContentText>
              {displayedText}
              {showShowMore && (
                <>
                  {!isExpanded && '... '}
                  <ShowMoreButtonInline onClick={() => setIsExpanded(!isExpanded)}>
                    {isExpanded ? (
                      <FormattedMessage defaultMessage="Show less" />
                    ) : (
                      <FormattedMessage defaultMessage="Show more" />
                    )}
                  </ShowMoreButtonInline>
                </>
              )}
            </ContentText>
          )}
        </ContentCopy>
        {buttonText && content.button_url && (
          <ContentButtonContainer>
            <GuestDirectoryExternalLinkButton
              label={buttonText}
              href={content.button_url}
              buttonColour={linkButtonColour}
              buttonBg={linkButtonBg}
              buttonBorder={linkButtonBorder}
            />
          </ContentButtonContainer>
        )}
      </TextColumn>
    </ContentRowWrapper>
  );
};

export default GuestDirectoryContentRow;
